import React, { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PencilIcon,
  CaretDownIcon,
  CaretRightIcon,
  DownloadIcon,
  FloppyDiskIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DotsThreeVerticalIcon,
  EnvelopeSimpleIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  DimensionType,
  TableConfig,
  DIMENSION_LABELS,
  CustomColumn,
  FormulaOperation,
  MultiYearForecastData,
  DimensionGroupingConfig,
  LineType,
  TableMetric,
  TABLE_METRIC_LABELS,
  DEFAULT_TABLE_METRIC,
  isVolumeTableMetric,
} from '@/types/rebateAnalytics';
import { useBreakdownCells } from './hooks/useBreakdownCells';
import BreakdownTable, {
  type BreakdownCellContext,
} from '../common/breakdown-tables/BreakdownTable';
import {
  createDataCellRenderer,
  aggregateCellValuesWithYOY,
} from './utils/rebateAnalyticsUtils';
import {
  createMonthMetadata,
  ALL_MONTHS,
} from '@/utils/rebateForecastingUtils';
import {
  generateMonthColumnOrder,
  generateMonthSubColumnOrder,
} from '@/utils/breakdown-transforms/monthOrdering';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TimeFilterPresetSelector } from './TimeFilterPreset';
import { Switch } from '@/components/ui/switch';
import {
  TimeFilterPreset,
  timeFilterPresetToSelectedMonths,
} from '@/utils/timeFilterPresets';
import TableConfigFormFields from './RebateAnalyticsTableConfigForm';
import {
  downloadTableCSVFromCells,
  downloadMultipleGroupCSVsFromCells,
  generateTableCSVFromCells,
} from './utils/csvExportFromCells';
import { generateTableName } from '@/utils/rebateAnalyticsTableUtils';
import { Label } from '../ui/label';
import SendTableEmailModal from './SendTableEmailModal';
import { Plural, T, Var, useGT } from 'gt-react';
import { useSendTableEmail } from '@/api/rebates/useSendTableEmail';

/**
 * Individual table renderer with data
 */
function RebateCustomBreakdownTableCard({
  config,
  onUpdate,
  onUpdateLabel,
  onDelete,
  multiYearForecastData,
  isCumulative,
  primaryLine = 'linear-regression',
  supplierIdToName,
  divisionIdToName,
  isCollapsed,
  onToggleCollapse,
  ruleTagIdToName,
  agreementTagIdToName,
  supplierTagIdToName,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  activeFilterCount,
  activeFilterNames,
  inactiveRuleNames,
}: {
  config: TableConfig;
  onUpdate?: (updatedConfig: Omit<TableConfig, 'id' | 'label'>) => void;
  onUpdateLabel?: (id: string, label: string) => void;
  /** Called when the user chooses Delete; parent owns the confirm dialog. */
  onDelete?: () => void;
  multiYearForecastData: MultiYearForecastData;
  // isCumulative indicates whether data is already cumulative (from parent transformation)
  isCumulative: boolean;
  // Primary line selection (determines which data source to use)
  primaryLine?: LineType | string;
  supplierIdToName: Map<string, string>;
  divisionIdToName: Map<string, string>;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  ruleTagIdToName?: Map<string, string>;
  agreementTagIdToName?: Map<string, string>;
  supplierTagIdToName?: Map<string, string>;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  activeFilterCount?: number;
  activeFilterNames?: string[];
  inactiveRuleNames?: Set<string>;
}) {
  const gt = useGT();

  const [isEditingConfig, setIsEditingConfig] = useState(false);
  // Time filter preset state - default to 'all' if not set
  const [timeFilterPreset, setTimeFilterPreset] = useState<
    TimeFilterPreset | undefined
  >(config.timeFilterPreset || 'all');
  // Show empty subdimensions toggle - default to false (hide empty by default)
  const [showEmptySubdimensions, setShowEmptySubdimensions] = useState(false);
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const sendEmailMutation = useSendTableEmail();

  // Custom columns management state
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const [_isManagingCustomColumns, _setIsManagingCustomColumns] =
    useState(false);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>(
    config.customColumns || [],
  );
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const [_editingCustomColumn, _setEditingCustomColumn] = useState<
    string | null
  >(null);
  const [customColumnForm, setCustomColumnForm] = useState({
    name: '',
    leftOperand: '',
    operation: FormulaOperation.SUBTRACT,
    rightOperand: '' as string | number,
    isRightOperandNumber: false,
  });

  // Edit state for form fields
  // WHY: Use local state only when editing; otherwise derive from config declaratively
  const [editPrimaryXDimension] = useState<DimensionType>(
    config.primaryXDimension ?? DimensionType.TIME,
  );
  const [editSecondaryXDimension, setEditSecondaryXDimension] = useState<
    DimensionType | undefined
  >(config.secondaryXDimension);
  const [editPrimaryYDimension, setEditPrimaryYDimension] =
    useState<DimensionType>(config.primaryYDimension ?? DimensionType.SUPPLIER);
  const [editSecondaryYDimension, setEditSecondaryYDimension] = useState<
    DimensionType | undefined
  >(config.secondaryYDimension);
  const [editMetric, setEditMetric] = useState<TableMetric>(
    config.metric ?? DEFAULT_TABLE_METRIC,
  );

  // Edit state for dimension grouping config
  const [editDimensionGroupingConfig, setEditDimensionGroupingConfig] =
    useState<
      Partial<
        Record<
          Exclude<DimensionType, DimensionType.TIME>,
          DimensionGroupingConfig
        >
      >
    >(config.dimensionGroupingConfig || {});

  // Edit state for custom name
  const [editCustomName, setEditCustomName] = useState<string | undefined>(
    undefined,
  );
  const [, setEditHasDimensionChanged] = useState<boolean>(false);

  // Handler for dimension grouping config changes
  const handleDimensionGroupingConfigChange = useCallback(
    (
      dimensionType: Exclude<DimensionType, DimensionType.TIME>,
      groupingConfig: DimensionGroupingConfig | undefined,
    ) => {
      setEditDimensionGroupingConfig((prev) => {
        const next = { ...prev };
        if (groupingConfig === undefined) {
          // Remove the dimension from config (no grouping)
          delete next[dimensionType];
        } else {
          // Set the grouping config
          next[dimensionType] = groupingConfig;
        }
        return next;
      });
    },
    [],
  );

  // Handler for secondary X dimension changes
  const handleSecondaryXDimensionChange = useCallback(
    (value: DimensionType | undefined) => {
      setEditSecondaryXDimension(value);
    },
    [],
  );

  const handleSecondaryYDimensionChange = useCallback(
    (value: DimensionType | undefined) => {
      setEditSecondaryYDimension(value);
    },
    [],
  );

  // WHY: Reset edit state declaratively when config changes and not editing
  // Use config values directly when not editing, local state when editing

  const effectiveTimeFilterPreset = isEditingConfig
    ? timeFilterPreset
    : config.timeFilterPreset || 'all';
  const effectiveCustomColumns = useMemo(
    () => (isEditingConfig ? customColumns : config.customColumns || []),
    [isEditingConfig, customColumns, config.customColumns],
  );

  // Reset local state when entering edit mode (sync with config)
  // WHY: Only sync when transitioning into edit mode, not on every config change
  const [prevIsEditingConfig, setPrevIsEditingConfig] =
    useState(isEditingConfig);
  if (isEditingConfig && !prevIsEditingConfig) {
    // Just entered edit mode - sync local state with config
    setEditSecondaryXDimension(config.secondaryXDimension);
    setEditPrimaryYDimension(
      config.primaryYDimension ?? DimensionType.SUPPLIER,
    );
    setEditSecondaryYDimension(config.secondaryYDimension);
    setEditMetric(config.metric ?? DEFAULT_TABLE_METRIC);
    setTimeFilterPreset(config.timeFilterPreset || 'all');
    setCustomColumns(config.customColumns || []);
    setEditDimensionGroupingConfig(config.dimensionGroupingConfig || {});
    // Initialize custom name: if config.label differs from auto-generated, use it
    const currentAutoGenerated = generateTableName(
      config.primaryXDimension ?? DimensionType.TIME,
      config.secondaryXDimension,
      config.primaryYDimension ?? DimensionType.SUPPLIER,
      config.secondaryYDimension,
      config.dimensionGroupingConfig,
      config.metric,
    );
    setEditCustomName(
      config.label !== currentAutoGenerated ? config.label : undefined,
    );
    setEditHasDimensionChanged(false); // Reset flag when entering edit mode
    setPrevIsEditingConfig(true);
  } else if (!isEditingConfig && prevIsEditingConfig) {
    // Just exited edit mode
    setPrevIsEditingConfig(false);
  }

  // Get forecast data for edit mode
  const forecastData = multiYearForecastData.currentYearData;

  // Auto-generate name for edit mode
  // WHY: Compute during render instead of using useEffect
  const editAutoGeneratedName = useMemo(
    () =>
      generateTableName(
        editPrimaryXDimension,
        editSecondaryXDimension,
        editPrimaryYDimension,
        editSecondaryYDimension,
        editDimensionGroupingConfig,
        editMetric,
      ),
    [
      editPrimaryXDimension,
      editSecondaryXDimension,
      editPrimaryYDimension,
      editSecondaryYDimension,
      editDimensionGroupingConfig,
      editMetric,
    ],
  );

  // Sync custom name when auto-generated name changes
  const prevEditAutoGeneratedNameRef = React.useRef<string>(
    editAutoGeneratedName,
  );
  React.useEffect(() => {
    const prevAutoGen = prevEditAutoGeneratedNameRef.current;
    const autoGenChanged = prevAutoGen !== editAutoGeneratedName;

    if (autoGenChanged) {
      // If name was auto-gen (matches previous auto-gen), auto-update
      if (editCustomName === undefined || editCustomName === prevAutoGen) {
        setEditCustomName(undefined); // Reset to use auto-generated
        setEditHasDimensionChanged(false); // Reset flag since we auto-updated
      } else {
        // Name is custom, mark that dimensions changed
        setEditHasDimensionChanged(true);
      }
    }

    prevEditAutoGeneratedNameRef.current = editAutoGeneratedName;
  }, [editAutoGeneratedName, editCustomName]);

  // Check for duplicate dimensions in edit mode
  const editHasDuplicates = useMemo(() => {
    const selected = [
      editPrimaryXDimension,
      editSecondaryXDimension,
      editPrimaryYDimension,
      editSecondaryYDimension,
    ].filter((d): d is DimensionType => d !== undefined);

    const seen = new Set<DimensionType>();
    for (const dim of selected) {
      if (seen.has(dim)) {
        return true;
      }
      seen.add(dim);
    }
    return false;
  }, [
    editPrimaryXDimension,
    editSecondaryXDimension,
    editPrimaryYDimension,
    editSecondaryYDimension,
  ]);

  // Custom column management handlers
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _handleAddCustomColumn = () => {
    if (!customColumnForm.name || !customColumnForm.leftOperand || !onUpdate) {
      return;
    }

    const newColumn: CustomColumn = {
      id: `custom-${Date.now()}`,
      name: customColumnForm.name,
      leftOperand: customColumnForm.leftOperand,
      operation: customColumnForm.operation,
      rightOperand: customColumnForm.isRightOperandNumber
        ? Number(customColumnForm.rightOperand)
        : customColumnForm.rightOperand,
    };

    const updatedColumns = [...customColumns, newColumn];
    setCustomColumns(updatedColumns);

    // Save to config immediately
    onUpdate({
      primaryXDimension: config.primaryXDimension ?? DimensionType.TIME,
      secondaryXDimension: config.secondaryXDimension,
      primaryYDimension: config.primaryYDimension ?? DimensionType.SUPPLIER,
      secondaryYDimension: config.secondaryYDimension,
      metric: config.metric,
      timeFilterPreset: effectiveTimeFilterPreset || 'all',
      customColumns: updatedColumns,
      dimensionGroupingConfig: config.dimensionGroupingConfig,
    });

    // Reset form
    setCustomColumnForm({
      name: '',
      leftOperand: '',
      operation: FormulaOperation.SUBTRACT,
      rightOperand: '',
      isRightOperandNumber: false,
    });
  };

  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _handleDeleteCustomColumn = (id: string) => {
    if (!onUpdate) {
      return;
    }

    const updatedColumns = customColumns.filter((col) => col.id !== id);
    setCustomColumns(updatedColumns);

    // Save to config immediately
    onUpdate({
      primaryXDimension: config.primaryXDimension ?? DimensionType.TIME,
      secondaryXDimension: config.secondaryXDimension,
      primaryYDimension: config.primaryYDimension ?? DimensionType.SUPPLIER,
      secondaryYDimension: config.secondaryYDimension,
      metric: config.metric,
      timeFilterPreset: effectiveTimeFilterPreset || 'all',
      customColumns: updatedColumns,
      dimensionGroupingConfig: config.dimensionGroupingConfig,
    });
  };

  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _getOperationSymbol = (operation: FormulaOperation): string => {
    switch (operation) {
      case FormulaOperation.ADD:
        return '+';
      case FormulaOperation.SUBTRACT:
        return '-';
      case FormulaOperation.MULTIPLY:
        return '×';
      case FormulaOperation.DIVIDE:
        return '÷';
      default:
        return '?';
    }
  };

  // Validate edit name
  const editDisplayName = editCustomName ?? editAutoGeneratedName;
  const editIsValidName = React.useMemo(() => {
    const trimmed = editDisplayName.trim();
    return trimmed !== '' && trimmed === editDisplayName; // No leading/trailing whitespace
  }, [editDisplayName]);

  const handleEditSave = () => {
    if (
      editHasDuplicates ||
      !editPrimaryYDimension ||
      !onUpdate ||
      !editIsValidName
    ) {
      return;
    }

    const updatedConfig = {
      primaryXDimension: editPrimaryXDimension,
      secondaryXDimension: editSecondaryXDimension,
      primaryYDimension: editPrimaryYDimension,
      secondaryYDimension: editSecondaryYDimension,
      metric: editMetric,
      timeFilterPreset: effectiveTimeFilterPreset || 'all',
      customColumns:
        effectiveCustomColumns.length > 0 ? effectiveCustomColumns : undefined,
      dimensionGroupingConfig:
        Object.keys(editDimensionGroupingConfig).length > 0
          ? editDimensionGroupingConfig
          : undefined,
    };

    onUpdate(updatedConfig);
    if (onUpdateLabel) {
      // Use custom name if provided and valid, otherwise use auto-generated
      const finalName = editCustomName?.trim() || editAutoGeneratedName;
      onUpdateLabel(config.id, finalName);
    }
    setIsEditingConfig(false);
  };

  const handleEditCancel = () => {
    setIsEditingConfig(false);
    setEditSecondaryXDimension(config.secondaryXDimension);
    setEditPrimaryYDimension(
      config.primaryYDimension ?? DimensionType.SUPPLIER,
    );
    setEditSecondaryYDimension(config.secondaryYDimension);
    setEditMetric(config.metric ?? DEFAULT_TABLE_METRIC);
    setEditDimensionGroupingConfig(config.dimensionGroupingConfig || {});
    setEditCustomName(undefined);
    setEditHasDimensionChanged(false);
  };

  // Use 4-dimension structure from config
  const primaryXDimension = config.primaryXDimension ?? DimensionType.TIME;
  const secondaryXDimension = config.secondaryXDimension;
  const primaryYDimension = config.primaryYDimension ?? DimensionType.SUPPLIER;
  const secondaryYDimension = config.secondaryYDimension;
  const metric = config.metric ?? DEFAULT_TABLE_METRIC;
  const isVolumeMetric = isVolumeTableMetric(metric);

  // Convert time filter preset to selectedMonths
  // WHY: Use effective preset (from config when not editing, from state when editing)
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _selectedMonthsFromPreset = React.useMemo(() => {
    if (effectiveTimeFilterPreset) {
      return timeFilterPresetToSelectedMonths(effectiveTimeFilterPreset);
    }
    // Fallback to legacy selectedMonths if preset not set
    return config.selectedMonths;
  }, [effectiveTimeFilterPreset, config.selectedMonths]);

  // Get breakdown cells directly - primary line selection is handled in useBreakdownCells
  const tableData = useBreakdownCells({
    multiYearForecastData,
    primaryXDimension,
    secondaryXDimension,
    primaryYDimension,
    secondaryYDimension,
    customColumns: effectiveCustomColumns,
    timeFilterPreset: effectiveTimeFilterPreset,
    dimensionGroupingConfig: config.dimensionGroupingConfig,
    fillMissingSubdimensions: showEmptySubdimensions,
    primaryLine: primaryLine as LineType,
    metric,
  });

  // Create month metadata from forecast data for cell renderer
  const monthMetadata = useMemo(() => {
    const forecastData = multiYearForecastData.currentYearData;
    if (!forecastData) {
      return [];
    }
    return createMonthMetadata(forecastData);
  }, [multiYearForecastData]);

  // Get dimension labels for display
  const groupLabel = DIMENSION_LABELS[primaryYDimension];
  const subRowLabel = secondaryYDimension
    ? DIMENSION_LABELS[secondaryYDimension]
    : undefined;

  // Render label with inactive badge for rules
  const renderLabel = useMemo(() => {
    if (!inactiveRuleNames || inactiveRuleNames.size === 0) return undefined;

    const rulesAreRows = primaryYDimension === DimensionType.RULE;
    const rulesAreSubRows = secondaryYDimension === DimensionType.RULE;

    if (!rulesAreRows && !rulesAreSubRows) return undefined;

    return ({
      value,
      isRow,
      isSubRow,
    }: {
      value: string;
      isRow?: boolean;
      isSubRow?: boolean;
    }) => {
      const isRuleLabel =
        (rulesAreRows && isRow) || (rulesAreSubRows && isSubRow);

      if (isRuleLabel && inactiveRuleNames.has(value)) {
        return (
          <span className="text-xs font-medium break-words flex items-center gap-1">
            <span className="opacity-60">{value}</span>
            <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-1 rounded">
              <T context="rebate breakdown table: inactive rule badge">
                Inactive
              </T>
            </span>
          </span>
        );
      }

      return <span className="text-xs font-medium break-words">{value}</span>;
    };
  }, [inactiveRuleNames, primaryYDimension, secondaryYDimension]);

  // Generate month ordering if primary X dimension is TIME
  const xDimOrder = useMemo(() => {
    if (primaryXDimension === DimensionType.TIME) {
      return generateMonthColumnOrder(tableData);
    }
    return undefined;
  }, [primaryXDimension, tableData]);

  const xDim2Order = useMemo(() => {
    if (secondaryXDimension === DimensionType.TIME) {
      return generateMonthSubColumnOrder(tableData);
    }
    return undefined;
  }, [secondaryXDimension, tableData]);

  // Calculate special columns (current month) when primary X dimension is TIME
  const specialColumns = useMemo(() => {
    if (primaryXDimension !== DimensionType.TIME) {
      return [];
    }

    const now = new Date();
    const actualCurrentYear = now.getUTCFullYear();
    const dataYear = multiYearForecastData.currentYear;

    // Only highlight current month when viewing current year data
    // No highlighting for historical years
    if (dataYear !== actualCurrentYear) {
      return [];
    }

    const currentMonthName = ALL_MONTHS[now.getUTCMonth()];

    // Find all columns that match the current month
    // Columns can be formatted as: "Jan", "Jan 2025", "Jan 2025 - Supplier A", etc.
    const matchingColumns: string[] = [];
    const columnSet = new Set<string>();
    tableData.forEach((cell) => {
      columnSet.add(cell.column);
    });

    columnSet.forEach((column) => {
      // Extract month from column name (handles formats like "Jan", "Jan 2025", "Jan 2025 - Supplier A")
      const parts = column.split(' - ');
      const monthPart = parts[0].trim();

      // Skip delta columns (contain Δ symbol)
      if (monthPart.includes('Δ')) {
        return;
      }

      // Check if it starts with the current month name
      if (monthPart.startsWith(currentMonthName)) {
        // Check if year matches current year (if year is present in column)
        const yearMatch = monthPart.match(/\d{4}/);
        if (!yearMatch || parseInt(yearMatch[0]) === actualCurrentYear) {
          matchingColumns.push(column);
        }
      }
    });

    return matchingColumns;
  }, [primaryXDimension, tableData, multiYearForecastData.currentYear]);

  // Calculate shaded column (current month and future months) when primary X dimension is TIME
  const shadedFromColumn = useMemo(() => {
    if (primaryXDimension !== DimensionType.TIME) {
      return undefined;
    }

    const now = new Date();
    const actualCurrentYear = now.getUTCFullYear();
    const dataYear = multiYearForecastData.currentYear;

    // Only shade from current month when viewing current year data
    // No shading for historical years
    if (dataYear !== actualCurrentYear) {
      return undefined;
    }

    const currentMonthName = ALL_MONTHS[now.getUTCMonth()];

    // Find the first column that matches the current month
    const columnSet = new Set<string>();
    tableData.forEach((cell) => {
      columnSet.add(cell.column);
    });

    for (const column of columnSet) {
      // Extract month from column name (handles formats like "Jan", "Jan 2025", "Jan 2025 - Supplier A")
      const parts = column.split(' - ');
      const monthPart = parts[0].trim();

      // Check if it starts with the current month name
      if (monthPart.startsWith(currentMonthName)) {
        // Check if year matches current year (if year is present in column)
        const yearMatch = monthPart.match(/\d{4}/);
        if (!yearMatch || parseInt(yearMatch[0]) === actualCurrentYear) {
          return column;
        }
      }
    }

    return undefined;
  }, [primaryXDimension, tableData, multiYearForecastData.currentYear]);

  // Create cell renderer that adapts to new context
  const renderDataCell = useMemo(() => {
    const currentYear = multiYearForecastData.currentYear;
    const previousYear = currentYear - 1;
    const originalRenderer = createDataCellRenderer(
      monthMetadata,
      currentYear,
      previousYear,
    );

    return (context: BreakdownCellContext) => {
      const { cell } = context;
      const metadata = cell.metadata as
        | {
            headerName?: string;
            isGroupLevel?: boolean;
            isCustomColumn?: boolean;
          }
        | undefined;

      // Create a mock old-style context for compatibility
      const mockOldContext = {
        header: {
          name: metadata?.headerName || cell.column,
          referenceId: metadata?.headerName || cell.column,
        },
        group: {
          label: cell.row,
          values: metadata?.isGroupLevel
            ? { [metadata.headerName || '']: cell.value }
            : undefined,
        },
        row: metadata?.isGroupLevel
          ? undefined
          : {
              label: cell.subRow || '',
              values: { [metadata?.headerName || '']: cell.value },
            },
        isGroupLevel: metadata?.isGroupLevel ?? false,
      };

      // Call original renderer with old-style signature
      const legacyValue = cell.value as
        number | { current: number; previous: number; delta: number } | null;
      return originalRenderer(legacyValue, mockOldContext);
    };
  }, [monthMetadata, multiYearForecastData]);

  // Get available column references for custom column dropdowns
  // Generate references from the flat data structure
  const columnReferenceOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    const seenColumns = new Set<string>();

    if (secondaryXDimension) {
      // Group references: A, B, C, ... (one per unique column value)
      let groupIndex = 0;
      tableData.forEach((cell) => {
        if (cell.subColumn === null && !seenColumns.has(cell.column)) {
          seenColumns.add(cell.column);
          const letter = String.fromCharCode(65 + groupIndex); // 65 = 'A'
          options.push({
            value: letter,
            label: `${letter} (${cell.column})`,
          });
          groupIndex++;
        }
      });
    } else {
      // Individual column references: 1, 2, 3, ... (one per unique column value)
      let colIndex = 0;
      tableData.forEach((cell) => {
        if (!seenColumns.has(cell.column)) {
          seenColumns.add(cell.column);
          colIndex++;
          options.push({
            value: String(colIndex),
            label: `${colIndex} (${cell.column})`,
          });
        }
      });
    }

    return options;
  }, [tableData, secondaryXDimension]);

  // Check if a custom column's references are valid with current headers
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _isCustomColumnValid = useCallback(
    (col: CustomColumn): boolean => {
      const availableRefs = new Set(
        columnReferenceOptions.map((opt) => opt.value),
      );

      // Check left operand
      if (!availableRefs.has(col.leftOperand)) {
        return false;
      }

      // Check right operand (if it's a column reference, not a number)
      if (
        typeof col.rightOperand === 'string' &&
        !availableRefs.has(col.rightOperand)
      ) {
        return false;
      }

      return true;
    },
    [columnReferenceOptions],
  );

  // CSV download handlers using extracted utilities
  const downloadCSV = () => {
    downloadTableCSVFromCells({
      cells: tableData,
      groupLabel,
      subRowLabel,
      tableName: config.label,
    });
  };

  const downloadMultipleCSV = () => {
    downloadMultipleGroupCSVsFromCells({
      cells: tableData,
      groupLabel,
      subRowLabel,
      tableName: config.label,
    });
  };

  // Email send handler
  const handleSendEmail = useCallback(
    (params: {
      to: string[];
      cc?: string[];
      bcc?: string[];
      subject: string;
      body: string;
      filename: string;
    }) => {
      // Generate CSV content
      const csvContent = generateTableCSVFromCells({
        cells: tableData,
        groupLabel,
        subRowLabel,
        tableName: config.label,
      });

      // Send email (toast notifications handled by useMutationApi)
      sendEmailMutation.mutate(
        {
          ...params,
          csvContent,
        },
        {
          onSuccess: () => {
            setShowEmailModal(false);
          },
        },
      );
    },
    [tableData, groupLabel, subRowLabel, config.label, sendEmailMutation],
  );

  // Generate default email content
  const defaultEmailSubject = `Rebate Analysis: ${config.label}`;
  const defaultEmailBody = `Please find attached the ${config.label} analysis.`;

  // Generate filename with time filter
  const timeFilterSuffix = effectiveTimeFilterPreset
    ? `_${effectiveTimeFilterPreset}`
    : '';
  const defaultEmailFilename = `${config.label}${timeFilterSuffix}`
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  return (
    <Card
      className="border overflow-hidden transition-shadow duration-200 hover:shadow-md w-full max-w-full min-w-0"
      style={{ maxWidth: '100%' }}
    >
      <CardHeader className="p-4 w-full min-w-0 max-w-full">
        {/* --- Top Row: Title + Actions --- */}
        <div className="flex justify-between items-center min-w-0">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            {/* Collapse / Expand button */}
            <button
              className="flex-shrink-0 text-muted-foreground hover:text-foreground p-0.5 rounded transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
              title={
                isCollapsed
                  ? gt('Expand', {
                      context: 'expand rebate analytics table card',
                    })
                  : gt('Collapse', {
                      context: 'collapse rebate analytics table card',
                    })
              }
            >
              {isCollapsed ? (
                <CaretRightIcon className="h-3.5 w-3.5" />
              ) : (
                <CaretDownIcon className="h-3.5 w-3.5" />
              )}
            </button>

            {/* Reorder buttons - vertical stack */}
            {(onMoveUp || onMoveDown) && (
              <div
                className="flex flex-col gap-0.5 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                {onMoveUp && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveUp();
                    }}
                    disabled={isFirst}
                    className="p-0 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded"
                    title={gt('Move up', {
                      context: 'reorder table card: move up in list',
                    })}
                  >
                    <ArrowUpIcon className="h-3 w-3" />
                  </button>
                )}
                {onMoveDown && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveDown();
                    }}
                    disabled={isLast}
                    className="p-0 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded"
                    title={gt('Move down', {
                      context: 'reorder table card: move down in list',
                    })}
                  >
                    <ArrowDownIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}

            {/* Title */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <CardTitle className="text-base font-medium truncate select-text">
                {config.label}
              </CardTitle>
            </div>
          </div>

          {/* Right-side controls - Actions Menu */}
          <div
            className="flex gap-2 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <DotsThreeVerticalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {/* Edit */}
                {!isEditingConfig && onUpdate && (
                  <>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditingConfig(true);
                      }}
                    >
                      <PencilIcon className="h-4 w-4 mr-2" />
                      <T context="rebate analytics table card menu">
                        Edit table
                      </T>
                    </DropdownMenuItem>
                    <div className="h-px bg-border my-1" />
                  </>
                )}

                {/* Download options */}
                <DropdownMenuItem onClick={downloadCSV}>
                  <DownloadIcon className="h-4 w-4 mr-2" />
                  <T context="rebate analytics table card menu">
                    Download table
                  </T>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadMultipleCSV}>
                  <DownloadIcon className="h-4 w-4 mr-2" />
                  <T context="rebate analytics table card menu: CSV by group dimension">
                    Download by <Var>{groupLabel}</Var>
                  </T>
                </DropdownMenuItem>

                {/* Send Email option */}
                <div className="h-px bg-border my-1" />
                <DropdownMenuItem onClick={() => setShowEmailModal(true)}>
                  <EnvelopeSimpleIcon className="h-4 w-4 mr-2" />
                  <T context="rebate analytics table card menu">
                    Send via email
                  </T>
                </DropdownMenuItem>

                {/* Show empty toggle */}
                <div className="h-px bg-border my-1" />
                <div
                  className="flex items-center justify-between px-2 py-2 cursor-pointer hover:bg-accent rounded-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowEmptySubdimensions(!showEmptySubdimensions);
                  }}
                >
                  <Label className="text-sm font-normal">
                    <T context="rebate analytics table: toggle empty subdimension rows">
                      Show empty
                      <Switch
                        checked={showEmptySubdimensions}
                        onCheckedChange={setShowEmptySubdimensions}
                        className="h-4 w-7"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </T>
                  </Label>
                </div>

                {/* Delete */}
                {onDelete && (
                  <>
                    <div className="h-px bg-border my-1" />
                    <DropdownMenuItem
                      onSelect={() => onDelete()}
                      className="text-destructive focus:text-destructive"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      <T context="rebate analytics table card menu destructive">
                        Delete table
                      </T>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* --- Secondary Row: Metadata + Pills --- */}
        <div className="flex items-center gap-2 mt-2 ml-5 flex-wrap min-w-0">
          {config.creatorName && (
            <p className="text-xs text-muted-foreground">
              <T context="rebate analytics table card metadata">
                Created by <Var>{config.creatorName}</Var>
              </T>
            </p>
          )}

          {isVolumeMetric && (
            <Badge
              variant="secondary"
              className="text-xs h-5 px-2 bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300"
            >
              {TABLE_METRIC_LABELS[metric]}
            </Badge>
          )}

          <Badge
            variant="secondary"
            className={`text-xs h-5 px-2 ${
              isCumulative
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
            }`}
          >
            {isCumulative ? (
              <T context="rebate table: cumulative rebate amounts">
                Cumulative
              </T>
            ) : (
              <T context="rebate table: month by month amounts">
                Month by Month
              </T>
            )}
          </Badge>

          {/* Current month indicator - only show when viewing current year data */}
          {(() => {
            const now = new Date();
            const actualCurrentYear = now.getUTCFullYear();
            const dataYear = multiYearForecastData.currentYear;

            // Only show current month badge for current year data
            if (dataYear !== actualCurrentYear) {
              return null;
            }

            const currentMonthName = ALL_MONTHS[now.getUTCMonth()];

            return (
              <Badge
                variant="secondary"
                className="text-xs h-5 px-2 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 font-medium"
              >
                <T context="rebate table badge: current month in forecast">
                  Current: <Var>{currentMonthName}</Var>{' '}
                  <Var>{actualCurrentYear}</Var>
                </T>
              </Badge>
            );
          })()}

          {/* Active filters count */}
          {activeFilterCount !== undefined && activeFilterCount > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Badge
                      variant="secondary"
                      className="text-xs h-5 px-2 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 flex items-center gap-1 cursor-help"
                    >
                      <InfoIcon className="h-3 w-3" />
                      <Var>{activeFilterCount}</Var>{' '}
                      <Plural
                        n={activeFilterCount}
                        one="filter"
                        other="filters"
                      />{' '}
                      <T context="rebate table: active filters suffix">
                        active
                      </T>
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1">
                    <div className="font-medium">
                      <T context="rebate table filter tooltip heading">
                        Filtering on:
                      </T>
                    </div>
                    {activeFilterNames && activeFilterNames.length > 0 ? (
                      <ul className="list-none space-y-0.5">
                        {activeFilterNames.map((name, index) => (
                          <li key={index}>
                            • <Var>{name}</Var>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div>
                        <T context="rebate table filter tooltip fallback">
                          • Filters active
                        </T>
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {!isEditingConfig && onUpdate && (
          <div
            className="flex items-center gap-4 mt-2 ml-5 min-w-0 flex-wrap"
            onClick={(e) => e.stopPropagation()}
          >
            <TimeFilterPresetSelector
              value={timeFilterPreset}
              onChange={(preset) => {
                // WHY: Update both local state and config when not in edit mode
                if (!isEditingConfig) {
                  setTimeFilterPreset(preset);
                }
                onUpdate({
                  primaryXDimension:
                    config.primaryXDimension ?? DimensionType.TIME,
                  secondaryXDimension: config.secondaryXDimension,
                  primaryYDimension:
                    config.primaryYDimension ?? DimensionType.SUPPLIER,
                  secondaryYDimension: config.secondaryYDimension,
                  metric: config.metric,
                  timeFilterPreset: preset,
                  dimensionGroupingConfig: config.dimensionGroupingConfig,
                });
              }}
              disabled={effectiveCustomColumns.length > 0}
              disabledTooltip={
                effectiveCustomColumns.length > 0
                  ? `Time filter cannot be changed while custom columns are defined. Remove custom columns first to change the time filter.`
                  : undefined
              }
              disableYOY={!multiYearForecastData.previousYearData}
              visiblePresets={['ytd', 'all', 'yoy', 'current']}
            />
          </div>
        )}
      </CardHeader>
      {isEditingConfig && (
        <CardContent className="border-t w-full max-w-full min-w-0 overflow-hidden">
          <TableConfigFormFields
            primaryXDimension={editPrimaryXDimension}
            secondaryXDimension={editSecondaryXDimension}
            primaryYDimension={editPrimaryYDimension}
            secondaryYDimension={editSecondaryYDimension}
            onSecondaryXDimensionChange={handleSecondaryXDimensionChange}
            onPrimaryYDimensionChange={(value) => {
              // In edit mode, we always need a primaryYDimension, so use default if undefined
              const newValue = value ?? DimensionType.SUPPLIER;
              setEditPrimaryYDimension(newValue);
              // Reset secondary Y dimension when primary is set to None (before conversion)
              if (!value) {
                setEditSecondaryYDimension(undefined);
              }
            }}
            onSecondaryYDimensionChange={handleSecondaryYDimensionChange}
            metric={editMetric}
            onMetricChange={setEditMetric}
            autoGeneratedName={editAutoGeneratedName}
            customName={editCustomName}
            onCustomNameChange={setEditCustomName}
            isValidName={editIsValidName}
            hasDuplicates={editHasDuplicates}
            showPreview={true}
            forecastData={forecastData}
            multiYearForecastData={multiYearForecastData}
            supplierIdToName={supplierIdToName}
            divisionIdToName={divisionIdToName}
            ruleTagIdToName={ruleTagIdToName}
            agreementTagIdToName={agreementTagIdToName}
            supplierTagIdToName={supplierTagIdToName}
            buttonLabel="Save"
            buttonIcon={<FloppyDiskIcon className="h-4 w-4" />}
            onSave={handleEditSave}
            onCancel={handleEditCancel}
            dimensionGroupingConfig={editDimensionGroupingConfig}
            onDimensionGroupingConfigChange={
              handleDimensionGroupingConfigChange
            }
          />
        </CardContent>
      )}
      {!isCollapsed && !isEditingConfig && (
        <CardContent className="pt-0 w-full max-w-full min-w-0">
          <div className="w-full max-w-full min-w-0 overflow-hidden">
            <BreakdownTable
              data={tableData}
              rowLabel={groupLabel}
              subRowLabel={subRowLabel}
              columnLabel={DIMENSION_LABELS[primaryXDimension]}
              subColumnLabel={
                secondaryXDimension
                  ? DIMENSION_LABELS[secondaryXDimension]
                  : undefined
              }
              title=""
              subtitle=""
              showCard={false}
              renderCell={renderDataCell}
              renderLabel={renderLabel}
              aggregateCellValues={aggregateCellValuesWithYOY}
              showXDim1Total={false}
              showYDim1Total={true}
              maxHeight={600}
              xDimOrder={xDimOrder}
              xDim2Order={xDim2Order}
              expandAllRows={false}
              fillMissingSubColumns={true}
              hideEmptySubdimensions={!showEmptySubdimensions}
              specialColumns={specialColumns}
              shadedFromColumn={shadedFromColumn}
            />
          </div>
        </CardContent>
      )}
      <SendTableEmailModal
        open={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        defaultSubject={defaultEmailSubject}
        defaultBody={defaultEmailBody}
        defaultFilename={defaultEmailFilename}
        isLoading={sendEmailMutation.isPending}
      />
    </Card>
  );
}

export default RebateCustomBreakdownTableCard;
